import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { GridConfig, GridRow } from '@inavista/components/grid';

// Cycle Counts popup → Cost Analysis → upload a count file → Cycle Count Upload on @inavista/components. Same columns,
// labels, order, widths, Item link, totals (Total / count of items / sum of counts), yellow invalid rows and toolbar
// (Columns, Search, Excel export, Clear filters) as the Syncfusion grid it replaces. Rows are the file's lines the page
// holds (uploadDetailData, LocalRowsGridApi). No paging (Martin): the old grid paged 20 at a time; this one scrolls.

export const CYCLE_COUNT_UPLOAD_DETAIL_URL = 'page://cycle-count-upload-detail';
export const CYCLE_COUNT_UPLOAD_DETAIL_PERSISTENCE_KEY = 'rims.service.cycle-count-upload-detail-lines';
/** The old row colour of a line with an invalid warehouse, item or count (screen and Excel). */
export const INVALID_LINE_COLOR = '#FFFF00';

export const CYCLE_COUNT_UPLOAD_DETAIL_COLUMNS: [string, string][] = [
  ['__rowKey', 'int'],
  ['warehouse', 'nvarchar'],
  ['itemCode', 'nvarchar'],
  ['count', 'nvarchar'],
];

const SHOWN = ['warehouse', 'itemCode', 'count'];

/** A line the file check flagged (the old queryCellInfo / excelQueryCellInfo rule). */
export function isInvalidUploadLine(row: GridRow): boolean {
  return row?.['itemCodeIsValid'] === false || row?.['countIsValid'] === false || row?.['warehouseIsValid'] === false;
}

export function buildCycleCountUploadDetailGridConfig(): GridConfig {
  return {
    apiBaseUrl: CYCLE_COUNT_UPLOAD_DETAIL_URL,
    clientSide: true,
    // The page numbers the lines (Warehouse, the old primary key, is the same on every line).
    keyColumn: '__rowKey',
    rowHeight: 30,
    defaultColumnOrder: SHOWN,
    hiddenByDefaultColumns: ['__rowKey'],
    columnLabels: { warehouse: 'Warehouse', itemCode: 'Item', count: 'Count' },
    // Every column left, as the old grid.
    columnAlign: Object.fromEntries(SHOWN.map(c => [c, { horizontal: 'left' as const, header: { horizontal: 'left' as const } }])),
    columnWidths: { warehouse: 100, itemCode: 100, count: 100 },
    stretchColumns: true,
    hyperlinkColumns: ['itemCode'],
    // The old footer: "Total" under Warehouse, the number of items, the sum of the counts (format N).
    totals: {
      itemCode: 'count',
      count: { of: rows => rows.reduce((sum, r) => sum + (parseFloat(String(r['count'] ?? '')) || 0), 0) },
    },
    totalsLabel: 'Total',
    columnNumberFormats: { count: { maximumFractionDigits: 0 } },
    rowToneFn: row => isInvalidUploadLine(row) ? { bg: INVALID_LINE_COLOR } : null,
    // The old toolbar: Columns, Refresh, Search, Excel export (every user), Clear filters. Refresh is not kept: it only
    // redrew the same lines.
    enableColumnsPicker: true,
    enableExport: true,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    multiSort: true,
    emptyStateMessage: 'No records to display',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: CYCLE_COUNT_UPLOAD_DETAIL_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
